import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy, getDocs } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { IoTimeOutline, IoCheckmarkCircleOutline, IoPencil, IoSearchOutline, IoCarSportOutline, IoCarOutline, IoBusOutline } from 'react-icons/io5';
import { FaMotorcycle } from 'react-icons/fa';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import './GestionVehiculosScreen.css';

// Función para elegir icono inteligente
const getVehiculoIcon = (vehiculo) => {
  const tipo = (vehiculo.tipo || '').toLowerCase();

  if (tipo === 'moto') {
    return <FaMotorcycle size={22} />;
  }
  if (tipo === 'suv') {
    return <IoCarOutline size={22} />; // Ajuste: SUV usa IoCarOutline
  }
  if (tipo === 'camion') {
    return <IoBusOutline size={22} />;
  }

  // Default Auto
  return <IoCarSportOutline size={22} />;
};

function GestionVehiculosScreen() {
  const navigate = useNavigate();
  const { userData } = useAuth(); // Usamos 'userData' del admin
  const [vehiculos, setVehiculos] = useState([]);
  const [residentesMap, setResidentesMap] = useState({}); // Mapa de ID -> Datos
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 1. Cargar Residentes del Condominio (para el lookup)
  useEffect(() => {
    if (!userData?.condominioId) return;

    const fetchResidentes = async () => {
      try {
        const qUsers = query(collection(db, "usuarios"), where("condominioId", "==", userData.condominioId));
        const snapshot = await getDocs(qUsers);
        const mapa = {};
        snapshot.forEach(doc => {
          const data = doc.data();
          mapa[doc.id] = {
            nombre: data.nombre,
            numeroCasa: data.numeroCasa || 'S/N', // Asumiendo que existe este campo
            unidad: data.unidad || ''
          };
        });
        setResidentesMap(mapa);
      } catch (e) {
        console.error("Error cargando residentes:", e);
      }
    };
    fetchResidentes();
  }, [userData]);

  useEffect(() => {
    // Cargar solo si tenemos los datos del admin (especialmente su condominioId)
    if (!userData?.condominioId) {
      setLoading(false);
      return;
    }

    // Consulta: Trae TODOS los vehículos del condominio del admin
    // Los ordena por estatus (pendientes primero)
    const q = query(
      collection(db, "vehiculos"),
      where("condominioId", "==", userData.condominioId),
      orderBy("estatus", "asc"), // "activo" va después de "pendiente" y otros
      orderBy("fechaRegistro", "desc") // Los más nuevos primero
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const listaVehiculos = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVehiculos(listaVehiculos);
      setLoading(false);
    }, (error) => {
      console.error("Error al cargar vehículos: ", error);
      if (error.code === 'failed-precondition') {
        alert("Error de base de datos. Se requiere un índice. Revisa la consola (F12) para ver el enlace y crearlo.");
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userData]);

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/gestion-vehiculos" />

      <main className="ks-main">
        <KondoBrandHeader title="Administración" showBackButton={false} />

        <div className="ks-scroll-area">
          <div className="vehiculos-dashboard-container">
            {/* --- Premium Banner --- */}
            <div className="muro-header-stitch">
              <div className="muro-hero-card">
                <div className="muro-hero-content">
                  <h1>Validación de Vehículos</h1>
                  <p>Control de acceso vehicular y gestión de estacionamientos.</p>
                </div>

                <IoCarSportOutline className="muro-hero-icon" />
              </div>
            </div>

            {/* --- Main Card --- */}
            <div className="vehiculos-card-list">

          {loading && (
            <div className="empty-state">
              <p>Cargando vehículos...</p>
            </div>
          )}

          {!loading && vehiculos.length === 0 && (
            <div className="empty-state">
              <IoCarSportOutline size={48} style={{ opacity: 0.3, marginBottom: 10 }} />
              <p>No hay vehículos para validar.</p>
            </div>
          )}

          {!loading && vehiculos.length > 0 && (
            isMobile ? (
              <div className="mobile-vehiculos-list">
                {vehiculos.map((vehiculo) => {
                  const esActivo = vehiculo.estatus === 'activo';
                  const residente = residentesMap[vehiculo.residenteId] || {};

                  return (
                    <div key={vehiculo.id} className="mobile-vehiculo-card">
                      <div className="mvc-header">
                        <div className="mvc-header-left">
                          <div className="vehiculo-icon-frame">
                            {getVehiculoIcon(vehiculo)}
                          </div>
                          <div className="mvc-title-group">
                            <span className="mvc-vehicle-name">{vehiculo.marca} {vehiculo.modelo}</span>
                            <span className="mvc-vehicle-year">Año {vehiculo.anio}</span>
                          </div>
                        </div>
                        <div className="mvc-actions">
                          <button
                            className="vehiculo-action-btn"
                            title="Validar / Editar Datos"
                            onClick={() => navigate(`/admin-vehiculos/editar/${vehiculo.id}`)}
                          >
                            <IoPencil size={18} />
                          </button>
                        </div>
                      </div>

                      <div className="mvc-body">
                        <div className="mvc-info-row">
                          <span className="mvc-info-label">Placas</span>
                          <span className="placas-tag">{vehiculo.placas || 'S/P'}</span>
                        </div>
                        <div className="mvc-info-row">
                          <span className="mvc-info-label">Residente</span>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <span className="mvc-info-value">{residente.numeroCasa ? `Casa ${residente.numeroCasa}` : 'Unidad Desconocida'}</span>
                            <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>{residente.nombre || 'Desconocido'}</span>
                          </div>
                        </div>
                        <div className="mvc-info-row">
                          <span className="mvc-info-label">Estatus</span>
                          <span className={`status-pill ${esActivo ? 'active' : 'pending'}`}>
                            {esActivo ? (
                              <><IoCheckmarkCircleOutline style={{ marginRight: 4 }} /> Activo</>
                            ) : (
                              <><IoTimeOutline style={{ marginRight: 4 }} /> Pendiente</>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>Tipo</th>
                      <th>Placas</th>
                      <th>Vehículo</th>
                      <th>Residente / Unidad</th>
                      <th>Estatus</th>
                      <th style={{ textAlign: 'right' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehiculos.map((vehiculo) => {
                      const esActivo = vehiculo.estatus === 'activo';
                      const residente = residentesMap[vehiculo.residenteId] || {};

                      return (
                        <tr key={vehiculo.id}>
                          <td>
                            <div className="vehiculo-icon-frame">
                              {getVehiculoIcon(vehiculo)}
                            </div>
                          </td>
                          <td>
                            <span className="placas-tag">{vehiculo.placas || 'S/P'}</span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontWeight: 600, color: '#1f2937' }}>
                                {vehiculo.marca} {vehiculo.modelo}
                              </span>
                              <span style={{ color: '#6b7280', fontSize: '0.85em' }}>
                                Año {vehiculo.anio}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontWeight: 600, color: '#374151' }}>
                                {residente.numeroCasa ? `Casa ${residente.numeroCasa}` : 'Unidad Desconocida'}
                              </span>
                              <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                                {residente.nombre || 'Desconocido'}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className={`status-pill ${esActivo ? 'active' : 'pending'}`}>
                              {esActivo ? (
                                <><IoCheckmarkCircleOutline style={{ marginRight: 4 }} /> Activo</>
                              ) : (
                                <><IoTimeOutline style={{ marginRight: 4 }} /> Pendiente</>
                              )}
                            </span>
                          </td>
                          <td>
                            <div className="action-cell-wrapper">
                              <button
                                className="vehiculo-action-btn"
                                title="Validar / Editar Datos"
                                onClick={() => navigate(`/admin-vehiculos/editar/${vehiculo.id}`)}
                              >
                                <IoPencil size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}
            </div>
          </div>
        </div>
      </main>

      <KondoBottomNav activeRoute="/gestion-vehiculos" />
    </div>
  );
}

export default GestionVehiculosScreen;